import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { EventItem } from '@klear/order-event-ui/event-item'
import { OrderEventDocumentsProvider } from '@klear/order-event-ui/order-event-documents-context'
import { MarkCompleteModal } from '@klear/order-event-ui/mark-complete-modal'
import { MarkFundedModal } from '@klear/order-event-ui/mark-funded-modal'
import { MarkFeesPaidModal } from '@klear/order-event-ui/mark-fees-paid-modal'
import { InvoiceOfferView } from '@klear/invoice-offer-ui/invoice-offer-view'
import { Card } from '@klear/ui/components/card'
import {
  ROUTE_ORDER_EVENTS,
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_COMPLETE,
  PERMISSION_ACTION_FUND,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_PLATFORM_FEE
} from '@klear/constants'
import { useAuth } from '@klear/ui/contexts/auth-context'
import type { OrderEventTimelineItem } from '@klear/order-event/order-event-timeline-tree'
import { timelineItemId } from '@klear/order-event/order-event-timeline-tree'
import { buildTimelineActionContext } from '@klear/order-event-ui/event-actions-availability'
import { timelineDocumentEventIds } from '@klear/order-event-ui/event-timeline-utils'

interface AdminOrderEventItemsProps {
  events: readonly OrderEventTimelineItem[]
  traderId: number
  orderId: number
  timelineEvents: readonly OrderEventTimelineItem[]
  appPrefix: string
  isEditing?: boolean
  expanded?: boolean
  currentEventId?: number
}

export default function AdminOrderEventItems({
  events,
  traderId,
  orderId,
  timelineEvents,
  appPrefix,
  isEditing = false,
  expanded = false,
  currentEventId
}: AdminOrderEventItemsProps) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const hasCompletePermission =
    user?.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_COMPLETE) ?? false
  const hasFundPermission = user?.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_FUND) ?? false
  const hasFeePermission = user?.hasPermission(PERMISSION_ENTITY_PLATFORM_FEE, PERMISSION_ACTION_ADD) ?? false
  const [markCompleteEvent, setMarkCompleteEvent] = useState<OrderEventTimelineItem | null>(null)
  const [markFundedEvent, setMarkFundedEvent] = useState<OrderEventTimelineItem | null>(null)
  const [markFeesPaidEvent, setMarkFeesPaidEvent] = useState<OrderEventTimelineItem | null>(null)
  const timelineActionContext = buildTimelineActionContext(timelineEvents)
  const documentEventIds = useMemo(() => timelineDocumentEventIds(timelineEvents), [timelineEvents])

  return (
    <OrderEventDocumentsProvider orderEventIds={documentEventIds} traderId={traderId}>
      {events.map((event) => (
        <Card key={timelineItemId(event)} className="p-4">
          <EventItem
            event={event}
            traderId={traderId}
            orderId={orderId}
            getEventUrl={(n) => `${appPrefix}/${ROUTE_ORDER_EVENTS}/${n.idDisplay}`}
            isEditing={isEditing}
            expanded={expanded}
            onView={(n) => navigate(`/${ROUTE_ORDER_EVENTS}/${n.envelope.idDisplay}`)}
            currentEventId={currentEventId}
            onMarkComplete={hasCompletePermission ? (n) => setMarkCompleteEvent(n) : undefined}
            onMarkFunded={hasFundPermission ? (n) => setMarkFundedEvent(n) : undefined}
            onMarkFeesPaid={hasFeePermission ? (n) => setMarkFeesPaidEvent(n) : undefined}
            invoiceOfferView={InvoiceOfferView}
            timelineActionContext={timelineActionContext}
          />
        </Card>
      ))}

      {markCompleteEvent ? (
        <MarkCompleteModal
          isOpen
          onClose={() => setMarkCompleteEvent(null)}
          onSuccess={() => setMarkCompleteEvent(null)}
          offerId={timelineItemId(markCompleteEvent)}
          offerIdDisplay={markCompleteEvent.envelope.idDisplay}
        />
      ) : null}
      {markFundedEvent ? (
        <MarkFundedModal
          isOpen
          onClose={() => setMarkFundedEvent(null)}
          onSuccess={() => setMarkFundedEvent(null)}
          offerId={timelineItemId(markFundedEvent)}
          offerIdDisplay={markFundedEvent.envelope.idDisplay}
        />
      ) : null}
      {markFeesPaidEvent ? (
        <MarkFeesPaidModal
          isOpen
          onClose={() => setMarkFeesPaidEvent(null)}
          onSuccess={() => setMarkFeesPaidEvent(null)}
          offerId={timelineItemId(markFeesPaidEvent)}
          offerIdDisplay={markFeesPaidEvent.envelope.idDisplay}
        />
      ) : null}
    </OrderEventDocumentsProvider>
  )
}
